/*!
 * Caja dinámica de precio para la oferta "compre uno y lleve el segundo a
 * ½ precio", sobre un producto de UNA sola variante + descuento automático
 * nativo de Shopify (Buy X Get Y). Agregado para Saludable Juntos
 * (27-sep-2026), a pedido de Melanie. Reemplaza al sistema de pestañas
 * anterior (assets/oferta-tabs-buyxgety.js, que ya no se carga en ningún
 * lado — se deja el archivo sin borrar por si hace falta revertir).
 *
 * Ronda 5 (27-sep-2026): antes había 2 comportamientos distintos según
 * cómo se llegó a la página ("simple" vs. "oferta" por "?quantity=" en la
 * URL) — ahora se simplificó a un solo comportamiento, siempre según la
 * cantidad actual del selector, sea cual sea el contexto de llegada
 * (la vista "oferta" ya llega con la cantidad pre-llenada en 2, ver
 * snippets/pdp-blocks.liquid → default_quantity, así que la caja aparece
 * de entrada en ese caso):
 *
 * - Caja verde debajo del selector de cantidad, oculta hasta llegar a 2 o
 *   más unidades, con el precio tachado (total sin descuento) y el precio
 *   final (con el 50% de descuento en cada 2da unidad ya aplicado) — se
 *   actualiza en vivo con cualquier cantidad (2, 3, 4...).
 * - Nota breve fija junto al selector, siempre visible, explicando el
 *   mecanismo del descuento (independiente de la cantidad).
 * - Texto entre paréntesis junto al precio de la cabecera del producto
 *   (snippets/product-price.liquid, span [data-oferta-title-price-suffix]),
 *   también recalculado en vivo, con el mismo total + aclaración del
 *   descuento — sólo aparece si hay 2 o más unidades.
 *
 * Cálculo (igual al que aplica el descuento nativo "Buy 1, get 1 at 50%
 * off", repetible por cada par de unidades en el carrito):
 *   unidadesConDescuento = floor(cantidad / 2)
 *   unidadesPrecioLleno  = cantidad - unidadesConDescuento
 *   total   = precioRegular × unidadesPrecioLleno + (precioRegular × 0.5) × unidadesConDescuento
 *   tachado = precioRegular × cantidad
 *
 * Ronda 8 (28-sep-2026): además, si el producto tiene configurado un
 * "regalo" real (metafield custom.oferta_gift_variant — hoy solo Himalaya
 * Berberine + Life Extension Cromo, ver el doc del proyecto), este script
 * escucha el evento nativo del carrito del tema (on:cart:loaded, tipo ADD)
 * y agrega automáticamente 1 unidad del regalo por cada 2 unidades del
 * producto que el cliente acaba de agregar — usando el mismo Cart store
 * público del tema (import "cart-store"), para que el ícono/badge del
 * carrito y el drawer se actualicen igual que con cualquier otro agregado.
 * La línea del regalo se marca con la property oculta "_es_regalo" (ver
 * snippets/cart-item.liquid, que la usa para mostrar "GRATIS" en vez de
 * precio y bloquear su cantidad).
 *
 * Es aditivo y a prueba de fallos: si no encuentra los elementos que
 * espera, no hace nada — nunca bloquea ni cambia lo que el cliente puede
 * comprar o pagar. El agregado automático del regalo corre en un
 * try/catch separado: si falla, no afecta el resto de la compra.
 */

import Cart from 'cart-store';

(function () {
  function injectStyles() {
    if (document.getElementById('oferta-deal-box-styles')) return;
    const style = document.createElement('style');
    style.id = 'oferta-deal-box-styles';
    style.textContent = `
      [data-oferta-deal-box] {
        margin: 10px 0 16px;
        padding: 10px 12px;
        border-radius: 6px;
        background: #eaf7ec;
        color: #1e7c34;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.4;
      }
      [data-oferta-deal-box] .oferta-deal-box__strike {
        text-decoration: line-through;
        color: #8a8a8a;
        font-weight: 400;
        margin-left: 8px;
      }
      [data-oferta-deal-note] {
        margin: 6px 0 0;
        font-size: 12px;
        color: #6b6b6b;
        font-weight: 400;
        line-height: 1.4;
      }
      [data-oferta-title-price-suffix] {
        font-size: 13px;
        font-weight: 600;
        color: #1e7c34;
        margin-left: 6px;
      }
    `;
    document.head.appendChild(style);
  }

  // Formatea un monto en CENTAVOS enteros como texto de moneda, sin pasar
  // nunca por una división de punto flotante antes de mostrarlo (evita
  // el error clásico de redondeo de JS, ej. (6748.5/100).toFixed(2)).
  function fmtCents(cents) {
    const wholePart = Math.trunc(cents / 100);
    const centsPart = Math.abs(cents % 100)
      .toString()
      .padStart(2, '0');
    return '$' + wholePart + '.' + centsPart;
  }

  // Ronda 8: agregado automático del regalo real (hoy, el Cromo de
  // Himalaya Berberine). Sólo actúa si el config trae un giftVariantId.
  // Escucha el evento que ya dispara el Cart store del tema cada vez que
  // se agrega algo al carrito, y sólo reacciona cuando lo agregado es
  // este mismo producto (por su variantId) — así no interfiere con
  // ningún otro "agregar al carrito" en la misma página (recomendaciones,
  // otro producto, etc.).
  function setupGiftAutoAdd(config) {
    if (!config.giftVariantId || !config.productVariantId) return;

    document.addEventListener('on:cart:loaded', async function (event) {
      try {
        const detail = (event && event.detail) || {};
        if (detail.type !== 'ADD' || !Array.isArray(detail.items)) return;

        const addedItem = detail.items.find(function (item) {
          return Number(item.id) === Number(config.productVariantId);
        });
        if (!addedItem) return;

        const giftQty = Math.floor(Number(addedItem.quantity) / 2);
        if (!Number.isFinite(giftQty) || giftQty < 1) return;

        await Cart.add({
          id: String(config.giftVariantId),
          quantity: String(giftQty),
          'properties[_es_regalo]': 'true',
          'properties[_regalo_de]': 'Oferta 2x1 + regalo'
        });
      } catch (e) {
        // A propósito silencioso: si falla el agregado automático del
        // regalo, la compra del producto principal ya se completó y no
        // debe verse afectada.
      }
    });
  }

  function init() {
    const configEl = document.querySelector('script[data-oferta-deal-config]');
    const box = document.querySelector('[data-oferta-deal-box]');
    const note = document.querySelector('[data-oferta-deal-note]');
    const titleSuffix = document.querySelector('[data-oferta-title-price-suffix]');
    if (!configEl || !box || !note) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    setupGiftAutoAdd(config);

    // regularPrice llega en centavos (tipo Money nativo de Liquid). Se
    // mantienen los cálculos en centavos enteros (nunca en dólares con
    // coma flotante) para que coincidan centavo a centavo con el mismo
    // cálculo que hace la tarjeta del catálogo en Liquid
    // ("price | times: 1.5 | round") — evita el típico error de
    // redondeo de punto flotante en JS (ej. 67.485 → "67.48" en vez de
    // "67.49").
    const regularPriceCents = Math.round(Number(config.regularPrice));
    if (!Number.isFinite(regularPriceCents) || regularPriceCents <= 0) return;

    const qtyEl = document.querySelector('quantity-input');
    const qtyInput = qtyEl ? qtyEl.querySelector('input[name="quantity"]') : null;
    if (!qtyEl || !qtyInput) return;

    injectStyles();

    // Nota fija: siempre visible, independiente de la cantidad y del
    // contexto de llegada (simple u oferta) — explica el mecanismo.
    note.hidden = false;
    note.textContent = '(Cada 2 que agregues: 1 a precio regular + 1 a mitad de precio, automático en el carrito)';

    function update() {
      const qty = Number(qtyInput.value);
      const hasDeal = Number.isFinite(qty) && qty >= 2;

      if (!hasDeal) {
        box.hidden = true;
        box.innerHTML = '';
        if (titleSuffix) {
          titleSuffix.hidden = true;
          titleSuffix.textContent = '';
        }
        return;
      }

      const discountedUnits = Math.floor(qty / 2);
      const fullPriceUnits = qty - discountedUnits;
      const halfUnitCents = Math.round(regularPriceCents * 0.5);
      const totalCents = regularPriceCents * fullPriceUnits + halfUnitCents * discountedUnits;
      const compareTotalCents = regularPriceCents * qty;

      box.hidden = false;
      box.innerHTML =
        '🎉 ¡Con ' +
        qty +
        ' obtienes la oferta! Total: <strong>' +
        fmtCents(totalCents) +
        '</strong>' +
        '<span class="oferta-deal-box__strike">' +
        fmtCents(compareTotalCents) +
        '</span>';

      if (titleSuffix) {
        titleSuffix.hidden = false;
        titleSuffix.textContent =
          '(' + qty + ' unidades: ' + fmtCents(totalCents) + ' con el descuento ya aplicado)';
      }
    }

    qtyInput.addEventListener('change', update);
    qtyInput.addEventListener('input', update);
    qtyEl.addEventListener('update', update);
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
